import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    useUrlLocalizer,
    useCurrentLanguage,
    useTranslator
} from 'tradera-lang/translate';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import { purchaseBin } from 'tradera-state/views/checkout/api';
import { fetchItemAndBidInfo } from 'tradera-state/views/view-item/reducer';
import binValidationParser from './bin-validation-parser';
import CheckFillIcon from '@tradera/blueprint/theme/icons2/check-fill.svg?jsx';
import NotifyIcon from '@tradera/blueprint/theme/icons2/notify.svg?jsx';
import { formatPrice } from '@tradera/blueprint/utils';
import * as analytics from '../../helpers/analytics';
import TopDescription from './bin-modal-top-description';
import SellerName from './bin-modal-seller-name';
import ItemDisplay from './bin-modal-item-display';
import ItemDisplayConfirmed from './bin-modal-item-display-confirmed';
import ShippingDisplay from './bin-modal-shipping-display';
import PaymentSummary from './bin-modal-payment-summary';
import BottomButtons from './bin-modal-bottom-buttons';
import { CardVerificationModal } from 'tradera-components/wacko-bidder/card-verification-modal';
import { BID_VALIDATION_RESULTS } from 'tradera-apps/tradera/components/vip/api/vip-api-constants';
import { selectPreferredCurrency } from 'tradera-state/multi-currency/selectors';

const BIN_FORM_STATES = {
    NOT_CONFIRMED: 'NOT_CONFIRMED',
    CONFIRMED: 'CONFIRMED',
    CONFIRMED_WITH_ERROR: 'CONFIRMED_WITH_ERROR'
};

const CenteredIcon = ({ children }) => {
    return (
        <div className="row mt-2 mt-md-2 mb-2">
            <div className="col-12 text-center">{children}</div>
        </div>
    );
};

const Separator = () => <div className="bg-gray-300" style={{ height: 1 }} />;

const BigSeparator = () => (
    <div className="row bg-gray-300 mx-n2 mx-md-0" style={{ height: 4 }} />
);

const CloseButton = ({ action, text }) => (
    <div className="row my-2 my-md-4">
        <div className="col-6 offset-3">
            <button
                type="button"
                className="btn btn-md w-100 btn-outline-dark"
                onClick={action}>
                {text}
            </button>
        </div>
    </div>
);

const BinModal = ({ onClose }) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const preferredCurrency = useSelector(selectPreferredCurrency);
    const [currentFormState, setCurrentFormState] = useState(
        BIN_FORM_STATES.NOT_CONFIRMED
    );
    const [error, setError] = useState(undefined);
    const [transactionId, setTransactionId] = useState(undefined);
    const { language } = useCurrentLanguage();

    const itemDetails = useSelector(state => state.views.viewItem.itemDetails);
    const {
        seller,
        itemMainImage,
        title: itemTitle,
        itemId,
        shippingOptions,
        fixedPrice
    } = itemDetails;
    const formattedPrice = formatPrice(fixedPrice, language, preferredCurrency);
    const buyerCountryCode = useSelector(
        state => state.shippingRegion.shippingCountry.countryCodeIso2
    );

    const onContinue = useCallback(() => {
        analytics.trackPayBin();
        window.location = toLocalizedUrl(
            `/Shopping/Checkout/SelectPaymentForTransaction?transactionId=${transactionId}`
        );
    }, [transactionId, toLocalizedUrl]);

    const onConfirm = () => {
        purchaseBin({
            itemId: itemId,
            googleAnalyticsData: analytics.getGaTrackingData()
        })
            .then(({ data }) => {
                setError(undefined);
                analytics.trackConfirmBin();
                setTransactionId(data.transactionId);
                setCurrentFormState(BIN_FORM_STATES.CONFIRMED);
                return;
            })
            .catch(e => {
                analytics.trackBinError();

                setError(e.response.data.responseStatus.errorCode);
                setCurrentFormState(BIN_FORM_STATES.CONFIRMED_WITH_ERROR);
            });
    };

    const closeModal = () => {
        if (currentFormState !== BIN_FORM_STATES.NOT_CONFIRMED) {
            dispatch(fetchItemAndBidInfo(itemId, false));
        }
        onClose();
    };

    const keepShopping = () => {
        analytics.trackKeepShopping();
        closeModal();
    };

    if (
        error === BID_VALIDATION_RESULTS.MAX_PLACED_BIDS_LIMIT ||
        error === BID_VALIDATION_RESULTS.BID_AMOUNT_LIMIT
    ) {
        return (
            <CardVerificationModal
                closeModal={closeModal}
                onCardVerified={onConfirm}
            />
        );
    }

    return (
        <DialogModal
            dismissOnOverlayClick={true}
            onDismiss={closeModal}
            mobileFullscreen={false}
            className="bin-modal justify-content-center mt-auto">
            <div className="bin-modal container p-2 px-md-4 m-md-2">
                {currentFormState === BIN_FORM_STATES.NOT_CONFIRMED && (
                    <>
                        <TopDescription
                            titleKey={'checkout_bin_title'}
                            descriptions={[
                                'checkout_bin_description1',
                                'checkout_bin_description2'
                            ]}
                            classes="mt-1 mt-md-2 mb-2"
                        />

                        <SellerName
                            memberId={seller.memberId}
                            alias={seller.alias}
                            cleanedAlias={seller.cleanedAlias}
                            hasBackground={false}
                            isCompany={seller.isCompany}
                            hasShop={seller.hasShop}
                        />

                        <Separator />

                        <ItemDisplay
                            itemImage={itemMainImage.med}
                            itemTitle={itemTitle}
                            unitPrice={fixedPrice}
                            language={language}
                            isFirstItem={true}
                            itemUrl={''}
                            quantity={1}
                        />

                        <Separator />

                        <ShippingDisplay
                            shippingOptions={shippingOptions}
                            buyerCountryCode={buyerCountryCode}
                        />

                        <Separator />

                        <PaymentSummary
                            formattedPrice={formattedPrice}
                            shippingOptions={shippingOptions}
                            totalPrice={fixedPrice}
                        />

                        <BigSeparator />

                        <BottomButtons
                            leftButton={{
                                action: closeModal,
                                text: t('checkout_bin_cancel'),
                                className: 'btn-outline-dark'
                            }}
                            rightButton={{
                                action: onConfirm,
                                text: t('checkout_bin_title'),
                                className: 'btn-success'
                            }}
                        />
                    </>
                )}

                {currentFormState === BIN_FORM_STATES.CONFIRMED && (
                    <>
                        <CenteredIcon>
                            <CheckFillIcon className="icon icon-success icon-xl" />
                        </CenteredIcon>

                        <TopDescription
                            titleKey={'checkout_bin_confirmedTitle'}
                            descriptions={['checkout_bin_confirmedDescription']}
                            classes="mt-2 mb-2"
                        />

                        <ItemDisplayConfirmed
                            itemImage={itemMainImage.med}
                            itemTitle={itemTitle}
                            formattedPrice={formattedPrice}
                            itemUrl={''}
                        />

                        <BigSeparator />

                        <BottomButtons
                            leftButton={{
                                action: keepShopping,
                                text: t('checkout_bin_button1'),
                                className: 'btn-success'
                            }}
                            rightButton={{
                                action: onContinue,
                                text: t('checkout_bin_button2'),
                                className: 'btn-outline-dark'
                            }}
                        />
                    </>
                )}

                {currentFormState === BIN_FORM_STATES.CONFIRMED_WITH_ERROR && (
                    <ErrorFeedback
                        error={error}
                        itemMainImage={itemMainImage}
                        itemTitle={itemTitle}
                        formattedPrice={formattedPrice}
                        onConfirm={onConfirm}
                        closeModal={closeModal}
                    />
                )}
            </div>
        </DialogModal>
    );
};

const ErrorFeedback = ({
    error,
    itemMainImage,
    itemTitle,
    formattedPrice,
    closeModal
}) => {
    const { t } = useTranslator();
    const { validationTitle, validationBody } = binValidationParser(error);

    return (
        <>
            <CenteredIcon>
                <NotifyIcon className="icon icon-danger icon-xl" />
            </CenteredIcon>

            <TopDescription
                titleKey={validationTitle}
                descriptions={[validationBody]}
                isError={true}
                classes="mt-2 mb-2"
            />

            <ItemDisplayConfirmed
                itemImage={itemMainImage.med}
                itemTitle={itemTitle}
                formattedPrice={formattedPrice}
                itemUrl={''}
            />

            <BigSeparator />

            <CloseButton text={t('checkout_bin_close')} action={closeModal} />
        </>
    );
};

export { Separator, BigSeparator };

export default BinModal;
